import { Button, Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import {
  EntityGithubActionsContent,
  isGithubActionsAvailable,
} from '@backstage-community/plugin-github-actions';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_OWNED_BY,
  RELATION_OWNER_OF,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TierSelector } from '../../techdocs/TierSelector';
import { VersionPicker } from '../../techdocs/VersionPicker';
import { RecentDocsTracker } from '../../techdocs/RecentDocsTracker';

import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';

import { ReadmeCard, isReadmeCardAvailable } from './ReadmeCard';
import { EntityReleasesContent, isReleasesAvailable } from '../releases';

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
      <TierSelector />
      <VersionPicker />
      <RecentDocsTracker />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const cicdContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isGithubActionsAvailable}>
      <EntityGithubActionsContent />
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in the Univerus Central documentation by clicking the button below."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3}>
    {entityWarningContent}
    <EntitySwitch>
      <EntitySwitch.Case if={isReadmeCardAvailable}>
        {/* Row 1: README (8) + Info sidebar (4) */}
        <Grid item xs={12} md={8}>
          <div className="readme-card-no-header">
            <ReadmeCard title=" " />
          </div>
        </Grid>
        <Grid item xs={12} md={4}>
          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12}>
              <EntityAboutCard />
            </Grid>
            <Grid item xs={12}>
              <EntityLinksCard />
            </Grid>
          </Grid>
        </Grid>
        {/* Row 2: Has Subcomponents full-width */}
        <Grid item xs={12}>
          <EntityHasSubcomponentsCard />
        </Grid>
      </EntitySwitch.Case>
      <EntitySwitch.Case>
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item xs={12}>
          <EntityHasSubcomponentsCard variant="gridItem" />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </Grid>
);

/**
 * Releases tab, offered on any entity that names a GitHub repo, and on systems
 * with components, where it merges their repos' releases. Group and domain
 * pages leave it out (see isReleasesAvailable).
 *
 * Several entities often share one repo — a system and its components do here
 * — and they all get the tab on purpose: having to hunt for the one entity
 * that happens to carry releases would be worse than seeing them twice.
 */
const releasesRoute = (
  <EntityLayout.Route
    if={isReleasesAvailable}
    path="/releases"
    title="Releases"
  >
    <EntityReleasesContent />
  </EntityLayout.Route>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/diagram" title="Diagram">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityCatalogGraphCard
            direction={Direction.TOP_BOTTOM}
            title="Component Diagram"
            height={700}
            maxDepth={1}
            relations={[
              RELATION_PART_OF,
              RELATION_HAS_PART,
              RELATION_API_CONSUMED_BY,
              RELATION_API_PROVIDED_BY,
              RELATION_CONSUMES_API,
              RELATION_PROVIDES_API,
              RELATION_DEPENDENCY_OF,
              RELATION_DEPENDS_ON,
              RELATION_OWNED_BY,
              RELATION_OWNER_OF,
            ]}
            unidirectional
          />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {releasesRoute}
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/diagram" title="Diagram">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityCatalogGraphCard
            direction={Direction.TOP_BOTTOM}
            title="Component Diagram"
            height={700}
            maxDepth={1}
            relations={[
              RELATION_PART_OF,
              RELATION_HAS_PART,
              RELATION_DEPENDENCY_OF,
              RELATION_DEPENDS_ON,
              RELATION_OWNED_BY,
              RELATION_OWNER_OF,
            ]}
            unidirectional
          />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {releasesRoute}
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/diagram" title="Diagram">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityCatalogGraphCard
            direction={Direction.TOP_BOTTOM}
            title="Component Diagram"
            height={700}
            maxDepth={1}
            relations={[
              RELATION_PART_OF,
              RELATION_HAS_PART,
              RELATION_API_CONSUMED_BY,
              RELATION_API_PROVIDED_BY,
              RELATION_CONSUMES_API,
              RELATION_PROVIDES_API,
              RELATION_DEPENDENCY_OF,
              RELATION_DEPENDS_ON,
              RELATION_OWNED_BY,
              RELATION_OWNER_OF,
            ]}
            unidirectional
          />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {releasesRoute}
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <EntitySwitch>
          <EntitySwitch.Case if={isReadmeCardAvailable}>
            {/* Row 1: README (8) + Info sidebar (4) */}
            <Grid item xs={12} md={8}>
              <div className="readme-card-no-header">
                <ReadmeCard title=" " />
              </div>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12}>
                  <EntityAboutCard />
                </Grid>
                <Grid item xs={12}>
                  <EntityLinksCard />
                </Grid>
              </Grid>
            </Grid>
            {/* Row 2: Providing/Consuming Components full-width */}
            <Grid item xs={12}>
              <EntityProvidingComponentsCard />
            </Grid>
            <Grid item xs={12}>
              <EntityConsumingComponentsCard />
            </Grid>
          </EntitySwitch.Case>
          <EntitySwitch.Case>
            <Grid item md={6} xs={12}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <EntityAboutCard />
                </Grid>
                <Grid item xs={12}>
                  <EntityLinksCard />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <EntityProvidingComponentsCard />
            </Grid>
            <Grid item xs={12}>
              <EntityConsumingComponentsCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/diagram" title="Diagram">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityCatalogGraphCard
            direction={Direction.TOP_BOTTOM}
            title="API Diagram"
            height={700}
            maxDepth={1}
            relations={[
              RELATION_API_CONSUMED_BY,
              RELATION_API_PROVIDED_BY,
              RELATION_CONSUMES_API,
              RELATION_PROVIDES_API,
              RELATION_OWNED_BY,
              RELATION_OWNER_OF,
            ]}
            unidirectional
          />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {releasesRoute}
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <EntitySwitch>
          <EntitySwitch.Case if={isReadmeCardAvailable}>
            <Grid item xs={12} md={8}>
              <div className="readme-card-no-header">
                <ReadmeCard title=" " />
              </div>
            </Grid>
            <Grid item xs={12} md={4} className="group-sidebar">
              <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12}>
                  <EntityGroupProfileCard />
                </Grid>
                <Grid item xs={12}>
                  <EntityLinksCard />
                </Grid>
                <Grid item xs={12}>
                  <EntityMembersListCard />
                </Grid>
              </Grid>
            </Grid>
          </EntitySwitch.Case>
          <EntitySwitch.Case>
            <Grid item xs={12} md={6}>
              <EntityGroupProfileCard variant="gridItem" />
            </Grid>
            <Grid item xs={12} md={6}>
              <EntityOwnershipCard variant="gridItem" />
            </Grid>
            <Grid item xs={12} md={6}>
              <EntityMembersListCard />
            </Grid>
            <Grid item xs={12} md={6}>
              <EntityLinksCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/diagram" title="Diagram">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityCatalogGraphCard
            direction={Direction.LEFT_RIGHT}
            title="Group Diagram"
            height={700}
            maxDepth={1}
            relations={[
              RELATION_PART_OF,
              RELATION_HAS_PART,
              RELATION_OWNED_BY,
              RELATION_OWNER_OF,
            ]}
            unidirectional
          />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <EntitySwitch>
          <EntitySwitch.Case if={isReadmeCardAvailable}>
            {/* Row 1: README (8) + Info sidebar (4) */}
            <Grid item xs={12} md={8}>
              <div className="readme-card-no-header">
                <ReadmeCard title=" " />
              </div>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12}>
                  <EntityAboutCard />
                </Grid>
                <Grid item xs={12}>
                  <EntityLinksCard />
                </Grid>
              </Grid>
            </Grid>
            {/* Row 2: Has Components/Apis/Resources full-width */}
            <Grid item xs={12}>
              <EntityHasComponentsCard />
            </Grid>
            <Grid item xs={12}>
              <EntityHasApisCard />
            </Grid>
            <Grid item xs={12}>
              <EntityHasResourcesCard />
            </Grid>
          </EntitySwitch.Case>
          <EntitySwitch.Case>
            <Grid item md={6}>
              <EntityAboutCard variant="gridItem" />
            </Grid>
            <Grid item md={6} xs={12}>
              <EntityLinksCard />
            </Grid>
            <Grid item xs={12}>
              <EntityHasComponentsCard variant="gridItem" />
            </Grid>
            <Grid item xs={12}>
              <EntityHasApisCard variant="gridItem" />
            </Grid>
            <Grid item xs={12}>
              <EntityHasResourcesCard variant="gridItem" />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityCatalogGraphCard
            direction={Direction.LEFT_RIGHT}
            title="System Diagram"
            height={700}
            maxDepth={1}
            relations={[
              RELATION_PART_OF,
              RELATION_HAS_PART,
              RELATION_API_CONSUMED_BY,
              RELATION_API_PROVIDED_BY,
              RELATION_CONSUMES_API,
              RELATION_PROVIDES_API,
              RELATION_DEPENDENCY_OF,
              RELATION_DEPENDS_ON,
              RELATION_OWNED_BY,
              RELATION_OWNER_OF,
            ]}
            unidirectional
          />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {releasesRoute}
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <EntitySwitch>
          <EntitySwitch.Case if={isReadmeCardAvailable}>
            {/* Row 1: README (8) + Info sidebar (4) */}
            <Grid item xs={12} md={8}>
              <div className="readme-card-no-header">
                <ReadmeCard title=" " />
              </div>
            </Grid>
            <Grid item xs={12} md={4}>
              <EntityAboutCard />
            </Grid>
            {/* Row 2: Has Systems full-width */}
            <Grid item xs={12}>
              <EntityHasSystemsCard />
            </Grid>
          </EntitySwitch.Case>
          <EntitySwitch.Case>
            <Grid item md={6}>
              <EntityAboutCard variant="gridItem" />
            </Grid>
            <Grid item md={6} xs={12}>
              <EntityLinksCard />
            </Grid>
            <Grid item xs={12}>
              <EntityHasSystemsCard variant="gridItem" />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/diagram" title="Diagram">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityCatalogGraphCard
            direction={Direction.LEFT_RIGHT}
            title="Domain Diagram"
            height={700}
            maxDepth={1}
            relations={[
              RELATION_PART_OF,
              RELATION_HAS_PART,
              RELATION_OWNED_BY,
              RELATION_OWNER_OF,
            ]}
            unidirectional
          />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
